import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { API_PUT } from "../../config/axiosConfig";
import { socket, sendSocketMessage, getWebSocketURL } from "../../socket";
import { joinQuestChannel, leaveQuestChannel, emitQuestAction } from "../../features/quests/questSocket";
import {
    changeURLByRoomId,
    exitFullScreen,
    enterFullScreen,
    isIOSDevice,
    getDeviceOrientation,
} from "../../utils/generalUtils";
import SpotifyInterface from "../../features/spotify/components/SpotifyControls";
import * as constants from "../../constants";
import { useDraggable } from "../../utils/useDraggable";
import { sendEventsLog, Logout } from "../../api/UserController";
import QuestCompleteImage from "../../assets/images/quests/Congratulations_Image.png";
import { resumePlayer } from "../../features/spotify/api/SpotifyController";
import { alterState } from "../../features/vagon/helpers/utils";
import { setToSessionStorage, getFromSessionStorage } from "../../hooks/useSessionStorage";
import Igt from "../../features/igt/Igt";
import LionGamingCasino from "../../features/lionGaming/LionGamingCasino";
import ArcadeGame from "../../features/arcadeGames/ArcadeGame";
import { getRoomSettingsById } from "../../api/RoomsController";
import CasinoLight from "../../features/casinoLight/CasinoLight";
import Streaming from "../../features/streaming/Streaming";
import Guestbook from "../../features/guestbook/Guestbook";
import SpotifyControls from "../../features/spotify/components/SpotifyControlsV2";
import DefaultStream from "../../features/vagon/DefaultStream";
import ExitIntraverseModal from "../../features/vagon/components/ExitIntraverseModal";
import Hallway from "../../features/hallway/Hallway";
import AvatarEditModal from "../presets/AvatarEditModal";
import { useRoomSettings } from "./RoomSettingsContext";
import { fetchVanityLinkBySlug } from "../../api/VanityLinkController";
import TZStreaming from "./TZStreaming";
import SmallIframeModal from "../../features/iFrameSmallModal/SmallIframeModal";
import { useQuestEngine } from "../../features/quests/useQuestEngine";
import AvatarPresetPage from "../presets/AvatarPresetPage";
import VanityAvatarPage from "../vanityLogin/VanityAvatarPage";

const RenderStreaming = ({ roomId: forcedRoomId }) => {
    const dispatch = useDispatch();
    const { roomName } = useParams(); // Can be a slug or a roomId
    const [resolvedRoomId, setResolvedRoomId] = useState(forcedRoomId || null);
    const userId = useSelector((state) => state.user?.userId);
    const token = useSelector((state) => state?.user?.token);
    const [showRetryStream, setShowRetryStream] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAvatar, setSelectedAvatar] = useState(getFromSessionStorage("selectedAvatar"));

    let videoLarge = false;

    const vanityLinkName = window.location.pathname.split("/").pop();
    const [currentScreen, setCurrentScreen] = useState("");

    const teddySwimRoomId = "992ee1dc-474b-46ed-9788-3b35739880f5";

    const elementRef = useRef(null);

    const [dimensions, setDimensions] = useState({ width: 1280, height: 720 });

    const focusRenderStream = useCallback(() => {
        const dcvContainer = document.getElementById("dcv-display");
        if (dcvContainer && typeof dcvContainer.focus === "function") {
            dcvContainer.focus();
        }
        if (window.Vagon?.focusIframe) {
            try {
                window.Vagon.focusIframe();
            } catch (_) {
                /* noop */
            }
        }
    }, []);

    const spotifyPlaying = useRef(false);

    // Refs to avoid stale closures in socket handlers
    const userIdRef = useRef(userId);
    const questRef = useRef(null);

    const [showCelebration, setShowCelebration] = useState(false);
    const [questVisible, setQuestVisible] = useState(true);
    const [questMinimized, setQuestMinimized] = useState(false);

    useEffect(() => {
        if (forcedRoomId) {
            setResolvedRoomId(forcedRoomId);
            return;
        }
        const resolveRoom = async () => {
            const isUUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
                roomName
            );

            if (isUUID) {
                setResolvedRoomId(roomName);
            } else {
                try {
                    const response = await fetchVanityLinkBySlug(roomName);
                    if (response?.data?.roomId) {
                        setResolvedRoomId(response.data.roomId);
                        console.log(`Resolved slug "${roomName}" to roomId: ${response.data.roomId}`);
                    } else {
                        console.error(`Could not resolve vanity slug: ${roomName}`);
                    }
                } catch (error) {
                    console.error(`Error fetching vanity link for slug ${roomName}:`, error);
                }
            }
        };

        if (roomName) resolveRoom();
    }, [roomName, forcedRoomId]);

    const { roomSettings, setRoomSettings } = useRoomSettings();

    const [showCornerButtons, setShowCornerButtons] = useState(true);
    const [landscape, setLandscape] = useState(false);
    const [fullScreen, setFullScreen] = useState(JSON.parse(getFromSessionStorage("fullscreen") || "false"));
    const [openBroadcastModal, setOpenBroadcastModal] = useState(false);
    const [playingMusic, setPlayingMusic] = useState(false);
    const [openSpotify, setOpenSpotify] = useState(false);

    const [isStreaming, setIsStreaming] = useState(false);
    const [igtSlot, setIgtSlot] = useState(null);
    const [lionGamingSlot, setLionGamingSlot] = useState(null);
    const [casinoLightSlot, setCasinoLightSlot] = useState(null);
    const [arcadeGameOpen, setArcadeGameOpen] = useState(false);
    const [isSmallModalOpen, setIsSmallModalOpen] = useState(false);
    const [smallIframeUrl, setSmallIframeUrl] = useState(
        // ""
        // "OPEN_SMALL_IFRAME, https://storage.googleapis.com/amadea_static/TyBigImages/TerraZero-One-Sheet_Intraverse.pdf, fullscreen"
        "OPEN_SMALL_IFRAME, https://open.spotify.com/embed/album/2Ui10TWQIabivg41HaHP41?utm_source=generator"
    );

    // Avatar and Hallway functionality
    const [avatarOpen, setIsAvatarOpen] = useState(false);
    const [avatarEditing, setAvatarEditing] = useState(false);
    const [updatedAvatarUrlSet, setUpdatedAvatarUrlSet] = useState(false);
    const [hallwayPrompt, setHallwayPrompt] = useState("");
    const [isHallwayOpen, setIsHallwayOpen] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);

    // These features are WIP, need to be made dynamically configurable
    const [teddyNoteOpen, setTeddyNoteOpen] = useState(false);
    const [openTeddySelfiePopup, setOpenTeddySelfiePopup] = useState(false);
    const [openContestModalState, setOpenContestModalState] = useState(false);
    const [isGuestbookOpen, setIsGuestbookOpen] = useState(false);

    // Dynamic link out functionality
    const [dynamicLinkOutUrl, setDynamicLinkOutUrl] = useState("");
    const [openLinkoutExitModal, setOpenLinkoutExitModal] = useState(false);

    const avatarPreset = getFromSessionStorage("selectedAvatar");
    const inviteSettings = localStorage.getItem("inviteSettings");
    const persistedUserId = userId || localStorage.getItem("dcvUserId");
    const persistedToken = token || localStorage.getItem("dcvToken");

    const [showResetQuest, setShowResetQuest] = useState(false);

    // Track when user is actually in-game (room loaded) vs loading screen
    const [isInGame, setIsInGame] = useState(false);

    const [loadedRoomId, setLoadedRoomId] = useState(roomSettings?.roomId || null);

    const setIgtSlotData = async (data) => {
        setIgtSlot(data);
    };
    const setLionGamingSlotData = async (data) => {
        setLionGamingSlot(data);
    };
    const setCasinoLightSlotData = async (data) => {
        setCasinoLightSlot(data);
    };

    const startSpotifyPlayer = useCallback(async (track) => {
        const type = constants.ALBUM;
        const spotifyToken = getFromSessionStorage("spotifyAccessToken");
        if (spotifyToken) {
            try {
                spotifyPlaying.current = true;
                const result = await resumePlayer(spotifyToken, track, type);
                if (result === 200) {
                    alterState(setPlayingMusic);
                }
            } catch {
                spotifyPlaying.current = false;
            }
        }
        setIsStreaming(false);
    }, []);

    // Helper to get a valid roomId from localStorage (filters out "undefined", "null", empty strings)
    const getValidLastRoomId = () => {
        const stored = localStorage.getItem("lastRoomId");
        if (!stored || stored === "undefined" || stored === "null" || !stored.trim()) {
            return null;
        }
        return stored;
    };

    // ✅ NEW: Quest engine (single source of truth)
    const quest = useQuestEngine({
        socket,
        userId,
        roomId: loadedRoomId || getValidLastRoomId() || resolvedRoomId || roomSettings?.roomId,
        unityRoomIdForLinkout: roomSettings?.roomId,
    });

    // On initial mount / room resolve: fetch quest
    useEffect(() => {
        let isMounted = true;
        async function fetchInitialQuest() {
            const roomIdToLoad = forcedRoomId || resolvedRoomId || roomSettings?.roomId;
            if (!roomIdToLoad || !userId) {
                return;
            }

            await quest.fetchQuestForRoom(roomIdToLoad);
        }

        fetchInitialQuest();
        setQuestVisible(true);
        setQuestMinimized(false);

        return () => {
            isMounted = false;
        };
    }, [forcedRoomId, resolvedRoomId, userId, roomSettings?.roomId]); // removed showResetQuest to avoid race condition with reset API

    // When loadedRoomId changes, refetch quest for that room
    useEffect(() => {
        if (loadedRoomId && userId) {
            quest.fetchQuestForRoom(loadedRoomId);
        }
    }, [loadedRoomId, userId]);

    useEffect(() => {
        const questId = quest.questState.questId;
        if (!socket || !userId || !questId) return;

        joinQuestChannel(socket, { questId, userId });

        return () => {
            leaveQuestChannel(socket, { questId, userId });
        };
    }, [socket, userId, quest.questState.questId]);

    // Helper to ensure socket is connected
    const ensureSocketConnected = useCallback(() => {
        if (!socket.connected) {
            try {
                socket.connect();
                console.log("Socket reconnected by ensureSocketConnected()");
            } catch (err) {
                console.error("Failed to reconnect socket", err);
            }
        }
    }, []);

    // Only ensure socket is connected once on mount and when userId changes
    // Do NOT trigger reconnect on UI state changes (modals, inputs, etc.)
    useEffect(() => {
        ensureSocketConnected();
    }, [userId, ensureSocketConnected]);

    // Keep refs in sync with current values to avoid stale closures
    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    useEffect(() => {
        questRef.current = quest;
    }, [quest]);

    const shouldIgnoreUnhandledSystemMessage = (messageStr) => {
        if (!messageStr) return true;
        // High-frequency / expected messages we do NOT want to log
        if (messageStr.includes(constants.AVATAR_ACTION)) return true;
        if (messageStr.includes(constants.ROOM_UNLOADED)) return true;
        if (messageStr.includes(constants.NEW_ROOM_LOADING)) return true;
        if (messageStr.includes(constants.NPC_OPEN)) return true;
        return false;
    };

    useEffect(() => {
        if (!avatarEditing) {
            setHallwayPrompt("I'm really loving my new avatar now! I look great!");
        }
    }, [avatarEditing]);

    useEffect(() => {
        focusRenderStream();

        const handleResize = () => {
            focusRenderStream();
            const dcvOuter = document.querySelector(".sc-dcvouterwrapper, #dcv-outer-wrapper");
            if (dcvOuter) {
                dcvOuter.dispatchEvent(new MouseEvent("click", { bubbles: true }));
            }
        };
        window.addEventListener("resize", handleResize);

        ensureSocketConnected();

        socket.on("connect", () => console.log("Connected to server:", socket.id));

        socket.on("broadcastAction", (data) => {
            console.log("Received broadcast action:", data);
            if (typeof data === "object" && data?.action === constants.OPEN_VIDEO_BROADCAST) {
                setIsStreaming(true);
            } else if (typeof data === "object" && data?.action === constants.END_VIDEO_BROADCAST) {
                setIsStreaming(false);
            }
        });

        socket.on("disconnect", (reason) => {
            console.warn("Socket disconnected. Reason:", reason);
            // Only attempt reconnect for transport errors, not intentional disconnects
            // Socket.IO will auto-reconnect for most cases; only intervene for specific reasons
            if (reason === "io server disconnect" || reason === "transport error") {
                // Server initiated disconnect or transport failure - try reconnecting
                setTimeout(() => {
                    if (!socket.connected) {
                        console.log("Attempting manual reconnect after server disconnect...");
                        socket.connect();
                    }
                }, 1000);
            }
            // For "io client disconnect" (intentional) or "ping timeout", let Socket.IO handle it
        });

        socket.on("quest_message", (payload) => {
            (async () => {
                try {
                    // 🔍 DEBUG: Uncomment to see all quest_message traffic
                    console.log("[quest_message] received:", JSON.stringify(payload, null, 2));

                    // ✅ let quest engine progress from Unity/Web quest-only events
                    // Use ref to avoid stale closure
                    await questRef.current?.ingestQuestMessage(payload);
                } catch (e) {
                    console.error("Error handling quest_message:", e);
                }
            })();
        });

        socket.on("system_message", (response) => {
            (async () => {
                try {
                    console.log("Received system message:", response, typeof response);

                    // Use refs to get current values and avoid stale closures
                    const currentUserId = userIdRef.current;
                    const currentQuest = questRef.current;

                    // ✅ NEW: let quest engine observe ALL system messages
                    await currentQuest?.ingestSystemMessage(response);

                    // Handle direct object format for SYSTEM_URL_OPEN
                    if (
                        typeof response === "object" &&
                        response?.type === "SYSTEM_URL_OPEN" &&
                        response?.userId === currentUserId &&
                        response?.url
                    ) {
                        window.open(response.url, "_blank");
                        return;
                    }

                    // Enhanced dynamic link out URL handling (similar to VagonStreamIntraverse)
                    if (typeof response === "string" && response.includes("$type")) {
                        try {
                            const arr = JSON.parse(response);
                            if (Array.isArray(arr) && typeof arr[0] === "string") {
                                const parsedResponse = JSON.parse(arr[0]);
                                console.log("Parsed response object:", parsedResponse);
                                const receivedUser = parsedResponse.UserId;
                                if (
                                    parsedResponse.$type === "8" &&
                                    receivedUser === currentUserId &&
                                    parsedResponse.Url
                                ) {
                                    setDynamicLinkOutUrl(parsedResponse.Url);
                                    setOpenLinkoutExitModal(true);
                                    return;
                                }
                            }
                        } catch (error) {
                            console.error("Error processing $type system message:", error);
                        }
                    }

                    // Fallback URL extraction for other message formats
                    const messageStr = typeof response === "string" ? response : JSON.stringify(response);
                    const urlMatch = messageStr.match(/"Url"\s*:\s*"([^"]+)"/);
                    const userIdMatch = messageStr.match(/"UserId"\s*:\s*"([^"]+)"/);

                    if (urlMatch && userIdMatch && userIdMatch[1] === currentUserId) {
                        const url = urlMatch[1];
                        window.open(url, "_blank");
                    }

                    if (typeof response !== "string") return;

                    const isMatch = (constant) => response.includes(constant);
                    const safeParse = () => {
                        try {
                            return JSON.parse(response);
                        } catch {
                            console.log("Failed to parse response:", response);
                            return null;
                        }
                    };

                    if (isMatch(constants.SYSTEM_URL_OPEN)) {
                        const parsed = safeParse();
                        if (parsed?.userId === currentUserId) {
                            window.open(parsed.url, "_blank");
                        }
                    }

                    // Keep your NEW_ROOM_LOADING flow as-is (room settings + joinRoom + loadedRoomId)
                    if (isMatch(constants.NEW_ROOM_LOADING)) {
                        const parsed = safeParse();
                        const eventType = parsed?.[0];
                        const roomId = parsed?.[0]?.split(":")[1];
                        const roomIdBackup = parsed?.[0]?.split(",")[1];
                        const receivedUser = parsed?.[1];
                        // Only store valid roomIds (avoid storing "undefined" or "null" strings)
                        const validRoomId = roomId || roomIdBackup;
                        if (validRoomId && validRoomId !== "undefined" && validRoomId !== "null") {
                            localStorage.setItem("lastRoomId", validRoomId);
                        }

                        console.log(
                            "Handling NEW_ROOM_LOADING for roomId:",
                            roomId,
                            "userId:",
                            receivedUser,
                            "backupRoomID:",
                            roomIdBackup
                        );
                        if (roomId && receivedUser === currentUserId) {
                            setIsLoading(false);
                            // User is now in-game, show quest modal
                            setIsInGame(true);
                            const fetchedSettings = await getRoomSettingsById(roomId);
                            if (fetchedSettings?.data) {
                                setRoomSettings(fetchedSettings.data);
                                await sendSocketMessage("joinRoom", roomId, currentUserId, "", "");
                                setLoadedRoomId(roomId);
                                console.log("loaded room", roomId);
                            }
                        }
                    }

                    // Handle vacantSession - user reconnecting to existing game session
                    if (typeof response === "string" && response.includes("vacantSession")) {
                        const parsed = safeParse();
                        // Check if this vacantSession message is for the current user
                        if (parsed?.userId === currentUserId || parsed?.[1] === currentUserId) {
                            console.log("Detected vacantSession reconnection, user is in-game");
                            setIsInGame(true);
                            // Use lastRoomId from localStorage for reconnection scenario
                            const cachedRoomId = getValidLastRoomId();
                            if (cachedRoomId && !loadedRoomId) {
                                setLoadedRoomId(cachedRoomId);
                            }
                        }
                    }

                    if (isMatch(constants.ROOM_UNLOADED)) {
                        await sendSocketMessage("leaveRoom", roomSettings?.roomId, currentUserId, "", "");
                        setRoomSettings(null);
                        setLoadedRoomId(null);
                    }

                    const parsed = safeParse();
                    const receivedUser = parsed?.[1];

                    if (!parsed || receivedUser !== currentUserId) return;

                    const logAndSend = async (constant, extraData = {}) =>
                        sendEventsLog(
                            "",
                            "",
                            currentUserId,
                            constant,
                            "info",
                            { response, ...extraData },
                            roomSettings?.roomId
                        );

                    switch (true) {
                        case isMatch(constants.OPEN_ARCADE_GAME):
                            setArcadeGameOpen(true);
                            await logAndSend(constants.OPEN_ARCADE_GAME);
                            break;

                        case isMatch(constants.OPEN_GUESTBOOK):
                            setIsGuestbookOpen(true);
                            await logAndSend(constants.OPEN_GUESTBOOK);
                            break;

                        case isMatch(constants.LOGOUT):
                            await sendSocketMessage("leaveRoom", roomSettings?.roomId, currentUserId, "", "");
                            await logAndSend(constants.LOGOUT);
                            await Logout(dispatch);
                            break;

                        case isMatch(constants.OPEN_SPOTIFY):
                        case isMatch(constants.CLOSE_SPOTIFY):
                            alterState(setOpenSpotify);
                            await logAndSend(
                                isMatch(constants.OPEN_SPOTIFY) ? constants.OPEN_SPOTIFY : constants.CLOSE_SPOTIFY
                            );
                            break;

                        case isMatch(constants.OPEN_TEDDY_SELFIE):
                            alterState(setOpenTeddySelfiePopup);
                            await logAndSend(constants.OPEN_TEDDY_SELFIE);
                            break;

                        case isMatch(constants.PDF_OPEN):
                        case isMatch(constants.PDF_CLOSE):
                            alterState(setShowCornerButtons);
                            await logAndSend(isMatch(constants.PDF_OPEN) ? constants.PDF_OPEN : constants.PDF_CLOSE);
                            break;

                        case isMatch(constants.OPEN_TEDDY_CONTEST):
                            sendSocketMessage("system_message", "", "", "", constants.UNITY_OPEN_WEB_INTERFACE);
                            alterState(setOpenContestModalState);
                            await logAndSend(constants.UNITY_OPEN_WEB_INTERFACE);
                            break;

                        case isMatch(constants.OPEN_TEDDY_NOTE):
                            sendSocketMessage("system_message", "", "", "", constants.UNITY_OPEN_WEB_INTERFACE);
                            alterState(setTeddyNoteOpen);
                            await logAndSend(constants.OPEN_TEDDY_NOTE);
                            break;

                        case isMatch(constants.FULL_SCREEN):
                            setFullScreen(true);
                            await logAndSend(constants.FULL_SCREEN);
                            break;

                        case isMatch(constants.SETUPCARD):
                            window.open("/wallet?source=web_unity");
                            await logAndSend(constants.SETUPCARD);
                            break;

                        case isMatch(constants.PAUSE_BACKGROUND_MUSIC):
                            document.getElementById("audio")?.pause?.();
                            await logAndSend(constants.PAUSE_BACKGROUND_MUSIC);
                            break;

                        case isMatch(constants.PLAY_BACKGROUND_MUSIC):
                            document.getElementById("audio")?.play?.();
                            await logAndSend(constants.PLAY_BACKGROUND_MUSIC);
                            break;

                        case isMatch(constants.ENTERING_NAV_MENU):
                            document.getElementById("audio")?.stop?.();
                            await logAndSend(constants.ENTERING_NAV_MENU);
                            break;

                        case isMatch(constants.SLOT):
                            setIgtSlotData(parsed?.[0]);
                            await logAndSend(constants.SLOT);
                            break;

                        case isMatch(constants.LIONGAME):
                            setLionGamingSlotData(parsed?.[0]);
                            await logAndSend(constants.LIONGAME);
                            break;

                        case isMatch(constants.CASINOLIGHT):
                            setCasinoLightSlotData(parsed?.[0]);
                            await logAndSend(constants.CASINOLIGHT);
                            break;

                        case isMatch(constants.OPEN_SMALL_IFRAME):
                            setIsSmallModalOpen(true);
                            setSmallIframeUrl(parsed?.[0]);
                            await logAndSend(constants.OPEN_SMALL_IFRAME);
                            break;

                        case isMatch(constants.EDIT_RPM_AVATAR):
                            setHallwayPrompt("I'm editing my avatar now");
                            setIsAvatarOpen(true);
                            setAvatarEditing(true);
                            setUpdatedAvatarUrlSet(false);
                            await logAndSend(constants.EDIT_RPM_AVATAR);
                            break;

                        case isMatch(constants.OPEN_HALLWAY):
                            setIsHallwayOpen(true);
                            await logAndSend(constants.OPEN_HALLWAY);
                            break;

                        default: {
                            const messageStr = typeof response === "string" ? response : JSON.stringify(response);

                            if (!shouldIgnoreUnhandledSystemMessage(messageStr)) {
                                console.warn("[SOCKET] Unhandled system_message:", messageStr);
                            }
                            break;
                        }
                    }
                } catch (err) {
                    console.error("Error handling WebSocket message:", err);
                }
            })();
        });

        return () => {
            window.removeEventListener("resize", handleResize);
            socket.off("connect");
            socket.off("broadcastAction");
            socket.off("disconnect");
            socket.off("system_message");
            socket.off("quest_message");
        };
    }, [userId, focusRenderStream, ensureSocketConnected, roomSettings?.roomId]);

    useEffect(() => {
        if (fullScreen === true || fullScreen === "true") {
            enterFullScreen(elementRef);
        } else if (
            (document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullScreenElement) &&
            !fullScreen
        ) {
            setToSessionStorage("fullscreen", false);
            exitFullScreen();
        }
    }, [fullScreen]);

    useEffect(() => {
        const anyModalOpen =
            openBroadcastModal ||
            openSpotify ||
            isStreaming ||
            !!igtSlot ||
            !!lionGamingSlot ||
            !!casinoLightSlot ||
            arcadeGameOpen ||
            avatarOpen ||
            teddyNoteOpen ||
            openTeddySelfiePopup ||
            openContestModalState ||
            isGuestbookOpen ||
            isHallwayOpen ||
            openLinkoutExitModal ||
            isInputFocused;

        if (!anyModalOpen) {
            const timer = setTimeout(() => {
                focusRenderStream();
            }, 150);

            return () => clearTimeout(timer);
        }
        return undefined;
    }, [
        openBroadcastModal,
        openSpotify,
        isStreaming,
        igtSlot,
        lionGamingSlot,
        casinoLightSlot,
        arcadeGameOpen,
        avatarOpen,
        teddyNoteOpen,
        openTeddySelfiePopup,
        openContestModalState,
        isGuestbookOpen,
        isHallwayOpen,
        openLinkoutExitModal,
        isInputFocused,
        focusRenderStream,
    ]);

    // Inject rotation CSS
    useEffect(() => {
        const styleId = "render-stream-rotation-style";
        if (!document.getElementById(styleId)) {
            const style = document.createElement("style");
            style.id = styleId;
            style.innerHTML = `
                #render-streaming-container.rotated {
                    background: black !important;
                    z-index: 100 !important;
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    const sendAchievementCompletion = useCallback(async () => {
        console.log("sending achievement completion- from function");

        if (Number(localStorage.getItem("questCompleted")) < 1) {
            const payloadShowBanner = { userId, payload: "SHOW_BANNER:QuestComplete" };
            const payloadShowAnimation = { userId, payload: "AVATAR_ACTION:Clapping" };
            const payloadShowCelebration = { userId, payload: "PLAY_VFX:Celebrate" };

            socket.emit("system_message", JSON.stringify(payloadShowBanner));
            socket.emit("system_message", JSON.stringify(payloadShowAnimation));
            socket.emit("system_message", JSON.stringify(payloadShowCelebration));

            try {
                await API_PUT(
                    `${process.env.REACT_APP_CORE_API_URI}/web/achievements/user/${userId}/13ac53d2-bfa7-4a17-9f04-14bf52b30039`,
                    { status: "Completed", count: 1 }
                );
            } catch (error) {
                console.error("Failed to unlock achievement", error);
            }
        }
    }, [userId]);

    useEffect(() => {
        if (!quest.questState.completed) return;
        localStorage.setItem("questCompleted", Number(localStorage.getItem("questCompleted")) + 1 || 1);
        sendAchievementCompletion();
        console.log("Quest completed! from useEffect");
        setShowCelebration(true);
        setShowResetQuest(false);

        const timer = setTimeout(() => {
            setShowCelebration(false);
            setShowResetQuest(true);
        }, 5000);

        return () => clearTimeout(timer);
    }, [quest.questState.completed, sendAchievementCompletion]);

    const [restartRequestId, setRestartRequestId] = useState(0);

    useEffect(() => {
        if (!restartRequestId) return;

        const effectiveRoomId = loadedRoomId || getValidLastRoomId() || resolvedRoomId || roomSettings?.roomId;
        if (!effectiveRoomId || !userId) return;

        (async () => {
            // 1) hard reset local quest state (forces render)
            quest.reset();

            // 2) refetch fresh quest
            await quest.fetchQuestForRoom(effectiveRoomId);

            // 3) show modal
            setQuestVisible(true);
            setQuestMinimized(false);
        })();
        // quest.reset and quest.fetchQuestForRoom are stable callbacks from useQuestEngine
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restartRequestId, loadedRoomId, userId, resolvedRoomId, roomSettings?.roomId]);

    const [windowInner, setWindowInner] = useState({ height: window.innerHeight, width: window.innerWidth });

    useEffect(() => {
        function handleResize() {
            setWindowInner({ height: window.innerHeight, width: window.innerWidth });
        }
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    function QuestOverlay({ step, onButtonClick, error, questName }) {
        const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
        const [orientation, setOrientation] = useState(getDeviceOrientation());

        useEffect(() => {
            function handleResize() {
                setIsMobile(window.innerWidth < 768);
                setOrientation(getDeviceOrientation());
            }
            window.addEventListener("resize", handleResize);
            window.addEventListener("orientationchange", handleResize);
            return () => {
                window.removeEventListener("resize", handleResize);
                window.removeEventListener("orientationchange", handleResize);
            };
        }, []);

        const shouldRotate = isMobile && orientation === "portrait";

        let initialPos;
        if (shouldRotate) {
            initialPos = { x: 0, y: 40 };
        } else if (isMobile) {
            initialPos = { x: 0, y: windowInner.height };
        } else {
            initialPos = { x: 0, y: windowInner.height };
        }

        const { dragProps } = useDraggable({
            initialPos,
            enabled: true,
        });

        if (!step || !questVisible) return null;

        let media = { type: null, url: "" };

        try {
            const meta = typeof step.metadata === "string" ? JSON.parse(step.metadata) : step.metadata;
            if (meta?.video) media = { type: "video", url: meta.video };
            else if (meta?.gif) media = { type: "gif", url: meta.gif };
            else if (meta?.image) media = { type: "image", url: meta.image };
            else if (meta?.img) media = { type: "image", url: meta.img };
            if (meta?.videoLarge === true) videoLarge = true;
            else videoLarge = false;
        } catch (e) {
            media = { type: null, url: "" };
        }

        if (questMinimized) {
            return <QuestMinimized onClick={() => setQuestMinimized(false)}>Resume Quest</QuestMinimized>;
        }

        const videoPresent = media.type === "video" || videoLarge;
        return (
            <QuestModalWrapper
                {...dragProps}
                $shouldRotate={shouldRotate}
                videoPresent={videoPresent}
                videoLarge={videoLarge}
                style={dragProps.style}
            >
                <QuestOverlayContainer shouldRotate={shouldRotate} videoPresent={videoPresent} videoLarge={videoLarge}>
                    <QuestHeader>
                        <QuestTitle>{questName || "Intraverse Quest"}</QuestTitle>
                        <QuestActions>
                            <QuestIcon onClick={() => setQuestMinimized(true)}>–</QuestIcon>
                            {/* <QuestIcon onClick={() => setQuestVisible(false)}>✕</QuestIcon> */}
                        </QuestActions>
                    </QuestHeader>

                    {media?.url ? (
                        <QuestVideoWrap>
                            {media.type === "video" ? (
                                <QuestVideo
                                    videoLarge={videoLarge}
                                    playsInline
                                    controls={false}
                                    autoPlay
                                    style={{ pointerEvents: "none" }}
                                >
                                    <source type="video/mp4" src={media.url} />
                                </QuestVideo>
                            ) : (
                                <QuestMediaImg
                                    src={media.url}
                                    alt="Quest media"
                                    draggable={false}
                                    style={{ pointerEvents: "none" }}
                                />
                            )}
                        </QuestVideoWrap>
                    ) : null}

                    <QuestText>{step.promptText}</QuestText>
                    {error && <QuestError>{error}</QuestError>}

                    {(() => {
                        const required = (step.requiredEvent || "").trim();
                        const isRoomChangeStep = required.startsWith("NEW_ROOM_LOADING:");
                        const isLinkOutStep = required.startsWith("OPEN_LINKOUT");
                        const isSmallIframeStep = required === "OPEN_SMALL_IFRAME";
                        const isButtonOnly = !required || required === "BUTTON_ONLY";

                        const getDefaultButtonLabel = () => {
                            if (isRoomChangeStep) return "Go";
                            if (isLinkOutStep) return "Open Link";
                            if (isSmallIframeStep) return "View Content";
                            return "Next";
                        };

                        return isButtonOnly || isRoomChangeStep || isLinkOutStep || isSmallIframeStep ? (
                            <QuestButton
                                disabled={isLoading}
                                isLoading={isLoading}
                                onClick={() => {
                                    const required = (step.requiredEvent || "").trim();
                                    const isRoomChangeStep = required.startsWith("NEW_ROOM_LOADING:");

                                    if (isRoomChangeStep) {
                                        setIsLoading(true);
                                        setTimeout(() => {
                                            setIsLoading(false);
                                        }, 13000);
                                    }

                                    onButtonClick();
                                }}
                            >
                                {isLoading ? "Teleporting..." : step.buttonLabel || getDefaultButtonLabel()}
                            </QuestButton>
                        ) : null;
                    })()}
                </QuestOverlayContainer>
            </QuestModalWrapper>
        );
    }

    if (showRetryStream) {
        return <DefaultStream userId={userId} token={token} roomId={resolvedRoomId} />;
    }

    return (
        <div
            style={{
                width: "100vw",
                height: "100vh",
                backgroundColor: "black",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 100,
            }}
        >
            {isGuestbookOpen && <Guestbook onClose={() => setIsGuestbookOpen(false)} />}
            {igtSlot && <Igt data={igtSlot} setIgtSlot={setIgtSlot} />}
            {lionGamingSlot && <LionGamingCasino data={lionGamingSlot} setLionGamingSlot={setLionGamingSlot} />}
            {arcadeGameOpen && <ArcadeGame onClose={() => setArcadeGameOpen(false)} />}
            {avatarOpen && vanityLinkName && (
                <VanityAvatarPage
                    noUsername
                    vanityLink={vanityLinkName}
                    source="editAvatarModal"
                    setCurrentScreen={setCurrentScreen}
                    setIsAvatarOpen={setIsAvatarOpen}
                />
            )}
            {avatarOpen && !vanityLinkName && (
                <AvatarPresetPage
                    setSelectedAvatar={setSelectedAvatar}
                    selectedAvatar={selectedAvatar}
                    forceLandScape
                    userId={userId}
                    source="editAvatarModal"
                    setIsAvatarOpen={setIsAvatarOpen}
                />
            )}
            {casinoLightSlot && <CasinoLight data={casinoLightSlot} setCasinoLightSlot={setCasinoLightSlot} />}
            {isStreaming && <Streaming streamId={roomSettings.broadcastId} />}
            {openLinkoutExitModal && (
                <ExitIntraverseModal onClose={() => setOpenLinkoutExitModal(false)} url={dynamicLinkOutUrl} />
            )}
            {roomSettings?.roomId === teddySwimRoomId ? (
                <SpotifyInterface
                    setFullScreen={setFullScreen}
                    landscape={landscape}
                    teddyNoteOpen={teddyNoteOpen}
                    setTeddyNoteOpen={setTeddyNoteOpen}
                    openTeddySelfiePopup={openTeddySelfiePopup}
                    setOpenTeddySelfiePopup={setOpenTeddySelfiePopup}
                    playingMusic={playingMusic}
                    openContestModalState={openContestModalState}
                    setOpenContestModalState={setOpenContestModalState}
                    openSpotify={openSpotify}
                    setOpenSpotify={setOpenSpotify}
                    spotifyTrack={roomSettings.spotifyTrack || null}
                    startSpotifyPlayer={startSpotifyPlayer}
                />
            ) : (
                <SpotifyControls setOpenSpotify={setOpenSpotify} openSpotify={openSpotify} />
            )}
            <Hallway setIsHallwayOpen={setIsHallwayOpen} isHallwayOpen={isHallwayOpen} hallwayPrompt={hallwayPrompt} />
            {isSmallModalOpen && (
                <SmallIframeModal
                    setIsSmallModalOpen={setIsSmallModalOpen}
                    smallIframeUrl={smallIframeUrl}
                    isSmallModalOpen={isSmallModalOpen}
                    setSmallIframeUrl={setSmallIframeUrl}
                    setOpenSpotify={setOpenSpotify}
                />
            )}
            {questVisible &&
                isInGame &&
                !quest.questState.loading &&
                quest.questState.questId &&
                !quest.questState.completed &&
                quest.questState.steps[quest.questState.currentStepIndex] && (
                    <QuestOverlay
                        step={quest.questState.steps[quest.questState.currentStepIndex]}
                        onButtonClick={() =>
                            quest.handleButtonClick({
                                setIsLoading,
                                openSmallIframe: (url) => {
                                    setSmallIframeUrl(url);
                                    setIsSmallModalOpen(true);
                                },
                            })
                        }
                        error={quest.questState.error}
                        questName={quest.questState.questName}
                    />
                )}
            {showCelebration && Number(localStorage.getItem("questCompleted")) < 1 && (
                <CelebrationOverlay>
                    <img
                        src={QuestCompleteImage}
                        alt="Quest Complete"
                        style={{ width: "380px", height: "auto", marginBottom: "10px" }}
                    />
                </CelebrationOverlay>
            )}
            {showResetQuest && quest.questState.completed && (
                <div style={{ position: "absolute", bottom: 40, left: 40, zIndex: 200 }}>
                    <button
                        style={{
                            padding: "20px 20px",
                            fontSize: "1rem",
                            background: "rgba(0, 0, 0, 0.8)",
                            color: "#ffffff",
                            border: "1px solid #ff4a9b",
                            borderRadius: "12px",
                            cursor: "pointer",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                        onClick={async () => {
                            const effectiveRoomId =
                                loadedRoomId || getValidLastRoomId() || resolvedRoomId || roomSettings?.roomId;
                            console.log("Restart Quest button clicked", {
                                questId: quest.questState.questId,
                                userId,
                                loadedRoomId,
                                effectiveRoomId,
                            });
                            try {
                                if (!quest.questState.questId || !userId || !effectiveRoomId) {
                                    console.warn("Restart Quest blocked - missing required values", {
                                        questId: quest.questState.questId,
                                        userId,
                                        effectiveRoomId,
                                    });
                                    return;
                                }

                                setShowCelebration(false);
                                setShowResetQuest(false);

                                await API_PUT(`${process.env.REACT_APP_CORE_API_URI}/web/quests/progress/reset`, {
                                    roomId: effectiveRoomId,
                                    userId,
                                    questId: quest.questState.questId,
                                });

                                // trigger the effect
                                setRestartRequestId((n) => n + 1);
                                localStorage.setItem("questCompleted", 0);
                            } catch (err) {
                                console.error("Failed to reset quest progress", err);
                            }
                        }}
                    >
                        Restart Quest
                    </button>
                </div>
            )}
            <TZStreaming roomId={resolvedRoomId} />
        </div>
    );
};

RenderStreaming.propTypes = {
    spotifyPlayer: PropTypes.object,
    roomId: PropTypes.string,
};

export default RenderStreaming;

// ---- styles unchanged ----

const QuestOverlayContainer = styled.div`
    position: absolute;
    left: ${(props) => (props.shouldRotate ? "40px" : "20px")};
    bottom: ${(props) => (props.shouldRotate ? "40px" : "20px")};
    max-width: ${(props) => (props.videoPresent && props.videoLarge ? "50vw" : "360px")};
    padding: 20px 20px;
    border-radius: 12px;
    border: 1px solid #ff4a9b;
    background: rgba(0, 0, 0, 0.8);
    color: #ffffff;
    z-index: 999999999;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    font-family: "Barlow", system-ui, sans-serif;
    transition: max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    @media (max-width: 800px) {
        max-width: ${(props) => (props.videoPresent && props.videoLarge ? "50vw" : "280px")};
        max-height: 350px;
    }
`;

const QuestTitle = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #ff4a9b;
    margin-bottom: 4px;
    opacity: 0.8;
`;

const QuestText = styled.div`
    font-size: 16px;
    line-height: 1.4;
    margin-bottom: 10px;
`;

const QuestError = styled.div`
    font-size: 12px;
    color: #ffb3b3;
    margin-bottom: 8px;
`;

const QuestButton = styled.button`
    padding: 8px 14px;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    background: ${(props) => (props.isLoading ? "transparent" : "#ff4a9b")};
    color: #ffffff;
    margin-top: 12px;
    transition:
        transform 0.1s ease,
        box-shadow 0.1s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
    }
`;

const CelebrationOverlay = styled.div`
    position: absolute;
    left: 50%;
    top: 40%;
    transform: translateX(-50%);
    padding: 18px 22px;
    background: transparent;
    color: #ffffff;
    z-index: 10000;
    text-align: center;
    min-width: 260px;
    font-family: "Barlow", system-ui, sans-serif;
`;

const QuestHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const QuestActions = styled.div`
    display: flex;
    gap: 8px;
`;

const QuestIcon = styled.button`
    background: transparent;
    border: none;
    color: white;
    font-size: 14px;
    cursor: pointer;
    opacity: 0.7;

    &:hover {
        opacity: 1;
    }
`;

const QuestMinimized = styled.div`
    position: absolute;
    left: 20px;
    bottom: 20px;
    padding: 10px 14px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    cursor: pointer;
    z-index: 99999999;
    font-size: 13px;
`;

const QuestVideoWrap = styled.div`
    margin: 18px 0 18px 0;
    border-radius: 14px;
    overflow: hidden;
    border: 2px solid rgba(255, 74, 155, 0.5);
    background: rgba(0, 0, 0, 0.45);
    width: 100%;
    display: flex;
    justify-content: center;
`;

const QuestVideo = styled.video`
    width: 40vw;
    height: ${(props) => (props.videoLarge ? "320px" : "auto")};
    max-width: 100%;
    border-radius: 12px;
    display: block;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
    background: black;
    @media (max-width: 900px) {
        width: 90vw;
        height: auto;
    }
`;

const QuestMediaImg = styled.img`
    width: 100%;
    height: auto;
    display: block;
`;

const QuestModalWrapper = styled.div`
    max-width: ${(props) => (props.videoPresent && props.videoLarge ? "900px" : "360px")};
    min-width: ${(props) => (props.videoPresent && props.videoLarge ? "600px" : "340px")};
    z-index: 99999999;
    position: fixed;
    ${(props) =>
        props.$shouldRotate
            ? `
        transform: rotate(-90deg) translateX(-100vh);
        transform-origin: top left;
        left: 350px;
        bottom: 340px;
        top: auto;
        width: 100vh;
        height: auto;
    `
            : `
        left: ${props.style?.left || 0}px;
        top: ${props.style?.top || 0}px;
    `}
    transition: max-width 0.3s cubic-bezier(0.4,0,0.2,1), min-width 0.3s cubic-bezier(0.4,0,0.2,1);
    @media (max-width: 800px) {
        max-width: ${(props) => (props.videoPresent && props.videoLarge ? "98vw" : "280px")};
        min-width: ${(props) => (props.videoPresent && props.videoLarge ? "0" : "260px")};
    }
`;
